<script setup lang="ts">
import { joinURL } from 'ufo';
import { amenities, currentSeasonWindows, lodgingPriceRange, petsAllowed, refuge, smokingAllowed } from '~/data/refuge';

// hreflang alternates, x-default, canonical, og:url and og:locale for all five
// locales. nuxt-seo-utils fills in the rest (og:type, og:site_name, and the
// og:/twitter: mirrors of each page's title and description).
const localeHead = useLocaleHead();

// nuxt-og-image is disabled (see nuxt.config.ts), so one static share image
// stands in for every page. Cropped to 1200x630 by Cloudinary, the size
// Facebook, LinkedIn and X all expect.
const shareImage = 'https://res.cloudinary.com/dlct0dnwt/image/upload/c_fill,w_1200,h_630,q_auto/v1743292001/refuge/2025/3PMA7289_iqzgsu.jpg';

useHead(() => ({
  htmlAttrs: localeHead.value.htmlAttrs,
  link: localeHead.value.link,
  meta: localeHead.value.meta,
}));

useSeoMeta({
  ogImage: shareImage,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  twitterCard: 'summary_large_image',
});

const { t } = useI18n();
const siteConfig = useSiteConfig();

// The default locale is served unprefixed, so this is the menu's canonical URL.
const menuPath = '/services/menu';

// Every figure below comes from data/refuge.ts, which is also what
// pages/index.vue, components/ContactInformation.vue and public/llms.txt read.
// They used to be literals here and again in each of those.
//
// A computed, not a plain array: the offer names are translated, and switching
// locale is a route change into the same component, so a plain array built at
// setup would keep the arrival language in the markup while the page changed
// around it. Same reasoning as pages/refuge/faq.vue.
//
// nuxt-schema-org adds the WebSite and WebPage nodes itself — with the locale
// cross-links, since @nuxtjs/i18n is installed — so neither is defined here.
useSchemaOrg(computed(() => [
  defineLocalBusiness({
    '@type': 'LodgingBusiness',
    name: "Katarraktis Mountain Refuge 'Babis Tsoupras'",
    email: refuge.contact.email,
    telephone: refuge.contact.phoneE164,
    image: shareImage,
    priceRange: lodgingPriceRange,
    currenciesAccepted: refuge.currency,
    paymentAccepted: 'Cash, Credit Card, Bank Transfer',
    // No street address or postcode: the refuge sits 8km up an unnamed road.
    // The geo coordinates below are what actually locates it.
    address: {
      addressLocality: 'Katarraktis, Tzoumerka',
      addressRegion: 'Epirus',
      addressCountry: 'GR',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: refuge.geo.latitude,
      longitude: refuge.geo.longitude,
      elevation: `${refuge.elevationMetres} m`,
    },
    sameAs: Object.values(refuge.social),

    // How many people the refuge sleeps. `numberOfRooms` would be the wrong
    // property — this is a dormitory, not a hotel with rooms — and so would a
    // bed count, which is not how the sleeping areas are arranged.
    maximumAttendeeCapacity: refuge.capacity,

    // June–October daily, November–May weekends, dated to the year they next
    // run in. See currentSeasonWindows() for why the boundaries are months.
    openingHoursSpecification: currentSeasonWindows(),

    // `description` is omitted rather than set to undefined where an amenity
    // has no caveat, so the emitted node stays clean.
    amenityFeature: amenities.map(amenity => ({
      '@type': 'LocationFeatureSpecification',
      name: amenity.name,
      value: amenity.value,
      ...(amenity.description ? { description: amenity.description } : {}),
    })),
    petsAllowed,
    smokingAllowed,

    // The full Menu graph is emitted by pages/services/menu.vue, in the
    // language being read; this is just the pointer to it.
    hasMenu: joinURL(siteConfig.url, menuPath),

    makesOffer: [
      {
        '@type': 'Offer',
        name: t('AccommodationWithFood'),
        price: refuge.rates.overnight.perNight,
        priceCurrency: refuge.currency,
        availability: 'https://schema.org/InStock',
      },
      {
        '@type': 'Offer',
        name: t('Camping'),
        price: refuge.rates.camping.perPerson,
        priceCurrency: refuge.currency,
        availability: 'https://schema.org/InStock',
      },
    ],
  }),
]));
</script>

<template>
  <NuxtLayout>
    <NuxtPage />
  </NuxtLayout>
</template>
